import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Loader2, BookOpen, Zap, Calendar, Play, FileText,
  Upload, Download, Eye, Copy, Archive, Trash2, CheckCircle2,
  Clock, Users, DollarSign, BarChart3, Settings2, Globe, Lock,
  Star, Package, Shield, UserPlus, Edit, ExternalLink, Save,
  AlertTriangle, GraduationCap, X, Plus, UserMinus, Search,
  RefreshCw, History, Video, MapPin, FileUp, Image, GripVertical,
  Layers, ChevronUp, ChevronDown, Link, Type, MessageCircle, AlertCircle, Send
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import AssignmentModal from "../../components/shared/AssignmentModal";
import ConfirmDeleteDialog from "../../components/shared/ConfirmDeleteDialog";

const courseTypeConfig = {
  "scorm_1.2": { label: "eLearning", icon: Zap, color: "bg-blue-50 text-blue-700 border-blue-200" },
  "scorm_2004": { label: "eLearning 2004", icon: Zap, color: "bg-blue-50 text-blue-700 border-blue-200" },
  ilt: { label: "Instructor-Led", icon: Calendar, color: "bg-amber-50 text-amber-700 border-amber-200" },
  video: { label: "Video", icon: Play, color: "bg-purple-50 text-purple-700 border-purple-200" },
  document: { label: "Document", icon: FileText, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const accessModelConfig = {
  subscription: { label: "Subscription", icon: Package, color: "bg-green-50 text-green-700 border-green-200", desc: "Included in tenant subscription" },
  purchasable: { label: "Purchasable", icon: DollarSign, color: "bg-amber-50 text-amber-700 border-amber-200", desc: "One-time purchase" },
  free: { label: "Free", icon: Globe, color: "bg-blue-50 text-blue-700 border-blue-200", desc: "Available to all" },
  restricted: { label: "Restricted", icon: Lock, color: "bg-red-50 text-red-700 border-red-200", desc: "Admin-assigned" },
};
const difficultyConfig = {
  beginner: "bg-green-50 text-green-700", intermediate: "bg-blue-50 text-blue-700",
  advanced: "bg-amber-50 text-amber-700", expert: "bg-red-50 text-red-700",
};
const CATEGORIES = ["Safety", "Technical Skills", "Compliance", "Leadership", "Operations", "Quality", "Environmental", "HR & Onboarding", "Maintenance", "General"];

function fmtTime(sec) { if (!sec) return "0m"; const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60); return h > 0 ? `${h}h ${m}m` : `${m}m`; }

import { useCallback } from "react";

// Small wrapper for SCORM preview iframe in admin view — sets window.API before loading content
const ScormPreviewIframe = ({ courseId, entryPoint }) => {
  const frameRef = useRef(null);
  useEffect(() => {
    // Define minimal SCORM API on window for the preview (admin view, no save needed)
    window.API = {
      LMSInitialize: () => 'true', LMSFinish: () => 'true',
      LMSGetValue: () => '', LMSSetValue: () => 'true',
      LMSCommit: () => 'true', LMSGetLastError: () => '0',
      LMSGetErrorString: () => 'No error', LMSGetDiagnostic: () => ''
    };
    window.API_1484_11 = {
      Initialize: () => 'true', Terminate: () => 'true',
      GetValue: () => '', SetValue: () => 'true',
      Commit: () => 'true', GetLastError: () => '0',
      GetErrorString: () => 'No error', GetDiagnostic: () => ''
    };
    return () => { delete window.API; delete window.API_1484_11; };
  }, []);

  const scormUrl = `/api/courses/${courseId}/scorm-content/${entryPoint}`;
  return (
    <iframe
      ref={frameRef}
      src={scormUrl}
      className="w-full rounded-lg border"
      style={{ height: "550px" }}
      title="SCORM Player"
      allow="fullscreen"
      data-testid="scorm-player-iframe"
    />
  );
};

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const scormInputRef = useRef(null);

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollSearch, setEnrollSearch] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Settings edit mode
  const [editing, setEditing] = useState(false);
  const [prerequisites, setPrerequisites] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // SCORM
  const [scormUploading, setScormUploading] = useState(false);
  const [scormPreviewUrl, setScormPreviewUrl] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [scormUploadProgress, setScormUploadProgress] = useState(0);
  const [scormUploadStage, setScormUploadStage] = useState("");
  const [showScormUploadDialog, setShowScormUploadDialog] = useState(false);
  // Custom Module Request (tenant admin)
  const [showCustomModuleDialog, setShowCustomModuleDialog] = useState(false);
  const [customModuleDesc, setCustomModuleDesc] = useState("");
  const [customModulePriority, setCustomModulePriority] = useState("normal");
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // Thumbnail
  const thumbnailInputRef = useRef(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  // Hero Image
  const heroInputRef = useRef(null);
  const [heroUploading, setHeroUploading] = useState(false);

  // Supporting Documents
  const docInputRef = useRef(null);
  const [docUploading, setDocUploading] = useState(false);

  // Modules / Course Builder
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [editModule, setEditModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({ title: "", type: "text", description: "", duration_minutes: 0, is_required: true, content_url: "", content_id: "" });
  const [moduleSaving, setModuleSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  // Assignment
  const [showAssign, setShowAssign] = useState(false);

  const isAdmin = user?.role === "super_admin" || user?.role === "tenant_admin";
  const base = user?.role === "super_admin" ? "/admin" : "/tenant";
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const isScorm = course?.course_type?.includes("scorm");

  const fetchCourse = async () => {
    try {
      const res = await api.get(`/courses/${courseId}`);
      setCourse(res.data);
      initEditForm(res.data);
    } catch (e) { toast.error("Course not found"); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const r = await api.get(`/courses/${courseId}/stats`); setStats(r.data); } catch {}
  };

  const fetchEnrollments = async () => {
    try { const r = await api.get(`/courses/${courseId}/enrollments`); setEnrollments(r.data || []); } catch {}
  };

  const initEditForm = (c) => {
    setEditForm({
      title: c.title || "", description: c.description || "", code: c.code || "",
      category: c.category || "General", course_type: c.course_type || "scorm_1.2",
      catalog_suite: c.catalog_suite || "", series: c.series || "",
      duration_hours: c.duration_hours || 1, instructor_name: c.instructor_name || "",
      access_model: c.access_model || "subscription", price: c.price || 0,
      difficulty_level: c.difficulty_level || "intermediate",
      self_enrollment_enabled: c.self_enrollment_enabled !== false,
      certificate_enabled: c.certificate_enabled || false,
      completion_criteria: c.completion_criteria || "progress",
      passing_score: c.passing_score || 0, max_attempts: c.max_attempts || 0,
      expiry_months: c.expiry_months || 0, renewal_required: c.renewal_required || false,
      tags: (c.tags || []).join(", "),
    });
  };

  useEffect(() => { fetchCourse(); fetchStats(); fetchEnrollments(); fetchModules(); }, [courseId]); // eslint-disable-line

  useEffect(() => {
    if (activeTab === "prerequisites") {
      api.get(`/courses/${courseId}/prerequisites`).then(r => setPrerequisites(r.data.prerequisites || [])).catch(() => {});
      api.get(`/courses?limit=200`).then(r => {
        const allCourses = r.data.courses || r.data || [];
        setAvailableCourses(allCourses.filter(c => c.id !== courseId));
      }).catch(() => {});
    }
  }, [activeTab, courseId]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        duration_hours: parseFloat(editForm.duration_hours) || 1,
        passing_score: parseInt(editForm.passing_score) || 0,
        max_attempts: parseInt(editForm.max_attempts) || 0,
        price: parseFloat(editForm.price) || 0,
        expiry_months: parseInt(editForm.expiry_months) || 0,
        tags: editForm.tags ? editForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      await api.put(`/courses/${courseId}`, payload);
      toast.success("Course updated");
      setEditing(false);
      fetchCourse();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handlePublish = async (publish) => {
    setActionLoading("publish");
    try { await api.put(`/courses/${courseId}/publish`, { publish }); toast.success(publish ? "Published" : "Unpublished"); fetchCourse(); }
    catch (e) { toast.error("Failed"); }
    finally { setActionLoading(""); }
  };

  const handleArchive = async () => {
    if (!window.confirm("Archive this course?")) return;
    setActionLoading("archive");
    try { await api.put(`/courses/${courseId}/archive`); toast.success("Archived"); fetchCourse(); }
    catch (e) { toast.error("Failed"); }
    finally { setActionLoading(""); }
  };

  const handleDuplicate = async () => {
    setActionLoading("duplicate");
    try { const r = await api.post(`/courses/${courseId}/duplicate`); toast.success("Duplicated"); navigate(`${base}/courses/${r.data.id}`); }
    catch (e) { toast.error("Failed"); }
    finally { setActionLoading(""); }
  };

  const handleDelete = async () => {
    setActionLoading("delete");
    try { await api.delete(`/courses/${courseId}`); toast.success("Deleted"); navigate(`${base}/courses`); }
    catch (e) { toast.error("Failed"); }
    finally { setActionLoading(""); setShowDeleteConfirm(false); }
  };


  const handleCustomModuleRequest = async () => {
    if (!customModuleDesc.trim()) { toast.error("Please describe your customization needs"); return; }
    setSubmittingRequest(true);
    try {
      await api.post("/requests/custom-module", {
        course_id: courseId,
        course_title: course?.title || "",
        description: customModuleDesc,
        priority: customModulePriority,
      });
      toast.success("Custom module request submitted! Our team will review it shortly.");
      setShowCustomModuleDialog(false);
      setCustomModuleDesc("");
    } catch { toast.error("Failed to submit request"); }
    finally { setSubmittingRequest(false); }
  };

  const handleScormUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.endsWith(".zip")) { toast.error("Select a .zip file"); return; }
    setScormUploading(true);
    setScormUploadProgress(0);
    setScormUploadStage("prepare");
    setShowScormUploadDialog(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      setScormUploadStage("upload");
      await api.post(`/courses/${courseId}/upload-scorm`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          if (pct < 50) {
            setScormUploadProgress(pct);
            setScormUploadStage("upload");
          } else if (pct < 80) {
            setScormUploadProgress(pct);
            setScormUploadStage("extract");
          } else if (pct < 95) {
            setScormUploadProgress(pct);
            setScormUploadStage("parse");
          } else {
            setScormUploadProgress(pct > 95 ? 95 : pct);
            setScormUploadStage("validate");
          }
        }
      });
      setScormUploadProgress(100);
      setScormUploadStage("done");
      toast.success("SCORM package uploaded successfully!");
      setTimeout(() => {
        setShowScormUploadDialog(false);
        setScormUploadProgress(0);
        setScormUploadStage("");
        fetchCourse();
      }, 1500);
    } catch (e) {
      setScormUploadStage("error");
      toast.error(e.response?.data?.detail || "Upload failed");
      setTimeout(() => { setShowScormUploadDialog(false); setScormUploadStage(""); }, 2000);
    }
    finally { setScormUploading(false); if (scormInputRef.current) scormInputRef.current.value = ""; }
  };

  const handlePreviewScorm = async () => {
    try {
      await api.get(`/courses/${courseId}/scorm-preview`);
      setScormPreviewUrl("ready"); // Just a truthy flag — actual URL handled by ScormPreviewIframe
      setShowPlayer(true);
    } catch (e) { toast.error(e.response?.data?.detail || "No SCORM content"); }
  };

  // Thumbnail upload
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) { toast.error("Select a JPG, PNG, WebP or GIF image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setThumbnailUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/courses/${courseId}/upload-thumbnail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thumbnail uploaded");
      setCourse(prev => ({ ...prev, thumbnail_url: r.data.thumbnail_url }));
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setThumbnailUploading(false); if (thumbnailInputRef.current) thumbnailInputRef.current.value = ""; }
  };

  // Hero image upload
  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) { toast.error("Select a JPG, PNG, WebP or GIF image"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    setHeroUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/courses/${courseId}/upload-hero`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Hero image uploaded");
      setCourse(prev => ({ ...prev, hero_image_url: r.data.hero_image_url }));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setHeroUploading(false); if (heroInputRef.current) heroInputRef.current.value = ""; }
  };

  // Supporting document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedExt = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt", ".csv"];
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedExt.includes(ext)) { toast.error(`Allowed: ${allowedExt.join(", ")}`); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("File must be under 25MB"); return; }
    setDocUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/courses/${courseId}/upload-document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setCourse(prev => ({ ...prev, supporting_documents: [...(prev.supporting_documents || []), r.data.document] }));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setDocUploading(false); if (docInputRef.current) docInputRef.current.value = ""; }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/courses/${courseId}/documents/${docId}`);
      toast.success("Document removed");
      setCourse(prev => ({ ...prev, supporting_documents: (prev.supporting_documents || []).filter(d => d.id !== docId) }));
    } catch (err) { toast.error("Failed to remove document"); }
  };

  // Module handlers
  const fetchModules = async () => {
    setModulesLoading(true);
    try { const r = await api.get(`/courses/${courseId}/modules`); setModules(r.data || []); }
    catch {} finally { setModulesLoading(false); }
  };

  const handleSaveModule = async () => {
    if (!moduleForm.title.trim()) { toast.error("Title required"); return; }
    setModuleSaving(true);
    try {
      if (editModule) {
        await api.put(`/courses/${courseId}/modules/${editModule.id}`, moduleForm);
        toast.success("Module updated");
      } else {
        await api.post(`/courses/${courseId}/modules`, moduleForm);
        toast.success("Module added");
      }
      setShowModuleDialog(false);
      fetchModules();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setModuleSaving(false); }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module?")) return;
    try { await api.delete(`/courses/${courseId}/modules/${moduleId}`); toast.success("Module deleted"); fetchModules(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleMoveModule = async (idx, direction) => {
    const newModules = [...modules];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newModules.length) return;
    [newModules[idx], newModules[swapIdx]] = [newModules[swapIdx], newModules[idx]];
    setModules(newModules);
    try {
      await api.put(`/courses/${courseId}/modules/reorder`, { module_ids: newModules.map(m => m.id) });
    } catch { toast.error("Reorder failed"); fetchModules(); }
  };

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (dropIdx) => {
    if (dragIndex === null || dragIndex === dropIdx) { setDragIndex(null); return; }
    const newModules = [...modules];
    const [moved] = newModules.splice(dragIndex, 1);
    newModules.splice(dropIdx, 0, moved);
    setModules(newModules);
    setDragIndex(null);
    try {
      await api.put(`/courses/${courseId}/modules/reorder`, { module_ids: newModules.map(m => m.id) });
    } catch { toast.error("Reorder failed"); fetchModules(); }
  };

  const openAddModule = () => {
    setEditModule(null);
    setModuleForm({ title: "", type: "text", description: "", duration_minutes: 0, is_required: true, content_url: "", content_id: "" });
    setShowModuleDialog(true);
  };

  const openEditModule = (mod) => {
    setEditModule(mod);
    setModuleForm({
      title: mod.title || "", type: mod.type || "text", description: mod.description || "",
      duration_minutes: mod.duration_minutes || 0, is_required: mod.is_required !== false,
      content_url: mod.content_url || "", content_id: mod.content_id || "",
    });
    setShowModuleDialog(true);
  };

  const handleRemoveEnrollment = async (enrollmentId) => {
    if (!window.confirm("Remove this enrollment?")) return;
    try { await api.delete(`/courses/${courseId}/enrollments/${enrollmentId}`); toast.success("Removed"); fetchEnrollments(); fetchStats(); }
    catch (e) { toast.error("Failed"); }
  };

  if (loading) return (
    <AdminLayout title="Course Details" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!course) return (
    <AdminLayout title="Not Found" variant={variant}>
      <div className="text-center py-16"><BookOpen className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" /><p className="text-sm text-muted-foreground">Course not found</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate(`${base}/courses`)}>Back to Courses</Button>
      </div>
    </AdminLayout>
  );

  const tc = courseTypeConfig[course.course_type] || courseTypeConfig["scorm_1.2"];
  const TypeIcon = tc.icon;
  const ac = accessModelConfig[course.access_model || "subscription"];
  const AccessIcon = ac.icon;
  const filteredEnroll = enrollments.filter(e => !enrollSearch || e.user_name?.toLowerCase().includes(enrollSearch.toLowerCase()) || e.user_email?.toLowerCase().includes(enrollSearch.toLowerCase()));

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "builder", label: `Course Builder (${modules.length})` },
    ...(isScorm ? [{ id: "content", label: "Content & SCORM" }] : []),
    { id: "prerequisites", label: "Prerequisites" },
    { id: "enrollments", label: `Enrollments (${enrollments.length})` },
    { id: "analytics", label: "Analytics" },
    { id: "discussions", label: "Discussions" },
    { id: "settings", label: "Settings" },
    { id: "distribution", label: "Distribution" },
  ];

  return (
    <AdminLayout title={course.title} variant={variant}>
      <div className="space-y-5 max-w-6xl" data-testid="course-detail-page">
        <Button variant="ghost" size="sm" className="text-xs gap-1.5 -ml-2" onClick={() => navigate(`${base}/courses`)} data-testid="back-btn">
          <ChevronLeft className="w-3.5 h-3.5" /> All Courses
        </Button>

        {/* ── HEADER ── */}
        <Card className="border shadow-none">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                {/* Thumbnail / Type Icon */}
                <div className="relative group/thumb shrink-0" data-testid="thumbnail-area">
                  {course.thumbnail_url ? (
                    <img src={course.thumbnail_url} alt={course.title} className="w-20 h-20 rounded-xl object-cover border" />
                  ) : (
                    <div className={cn("w-20 h-20 rounded-xl flex items-center justify-center", tc.color)}>
                      <TypeIcon className="w-8 h-8" />
                    </div>
                  )}
                  <button
                    onClick={() => thumbnailInputRef.current?.click()}
                    className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                    disabled={thumbnailUploading}
                    data-testid="thumbnail-upload-trigger"
                  >
                    {thumbnailUploading ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Image className="w-5 h-5 text-white" />}
                  </button>
                  <input ref={thumbnailInputRef} type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" data-testid="thumbnail-file-input" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {course.is_archived ? (
                      <Badge className="text-[10px] px-2 py-0.5 bg-muted text-muted-foreground border">Archived</Badge>
                    ) : course.is_published ? (
                      <Badge className="text-[10px] px-2 py-0.5 bg-green-50 text-green-700 border-green-200 gap-1" data-testid="status-badge"><CheckCircle2 className="w-3 h-3" />Published</Badge>
                    ) : (
                      <Badge className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 border-amber-200 gap-1" data-testid="status-badge"><Edit className="w-3 h-3" />Draft</Badge>
                    )}
                    <Badge className={cn("text-[10px] px-2 py-0.5 border gap-1", tc.color)}><TypeIcon className="w-3 h-3" />{tc.label}</Badge>
                    <Badge className={cn("text-[10px] px-2 py-0.5 border gap-1", ac.color)}><AccessIcon className="w-3 h-3" />{ac.label}</Badge>
                    {course.code && <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{course.code}</span>}
                  </div>
                  <h1 className="text-xl font-bold truncate" data-testid="course-title">{course.title}</h1>
                  {course.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{course.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                {!course.is_published && !course.is_archived && (
                  <Button onClick={() => handlePublish(true)} disabled={!!actionLoading} className="gap-1.5 h-8 text-xs" data-testid="publish-btn">
                    {actionLoading === "publish" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Publish
                  </Button>
                )}
                {course.is_published && (
                  <Button variant="outline" onClick={() => handlePublish(false)} disabled={!!actionLoading} className="gap-1.5 h-8 text-xs" data-testid="unpublish-btn">
                    <Eye className="w-3.5 h-3.5" /> Unpublish
                  </Button>
                )}
                <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={handleDuplicate} disabled={!!actionLoading} data-testid="duplicate-btn">
                  <Copy className="w-3.5 h-3.5" /> Duplicate
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={handleArchive} disabled={!!actionLoading} data-testid="archive-btn">
                  <Archive className="w-3.5 h-3.5" /> Archive
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDeleteConfirm(true)} disabled={!!actionLoading} data-testid="delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </Button>
                {user?.role === "tenant_admin" && (
                  <Button size="sm" className="gap-1.5 text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowCustomModuleDialog(true)} data-testid="request-custom-module-btn">
                    <Send className="w-3.5 h-3.5" /> Request Custom Module
                  </Button>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5 pt-5 border-t">
              {[
                { label: "Enrolled", value: stats?.total_enrollments ?? 0, icon: Users, color: "text-blue-600 bg-blue-50" },
                { label: "Completed", value: stats?.completed ?? 0, icon: CheckCircle2, color: "text-green-600 bg-green-50" },
                { label: "Completion", value: `${stats?.completion_rate ?? 0}%`, icon: BarChart3, color: "text-primary bg-primary/10" },
                { label: "Duration", value: `${course.duration_hours}h`, icon: Clock, color: "text-amber-600 bg-amber-50" },
                ...(course.access_model === "purchasable" ? [{ label: "Revenue", value: `$${(stats?.revenue ?? 0).toFixed(0)}`, icon: DollarSign, color: "text-green-600 bg-green-50" }] : [{ label: "Avg Score", value: stats?.avg_score ?? 0, icon: Star, color: "text-amber-600 bg-amber-50" }]),
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", s.color)}><s.icon className="w-4 h-4" /></div>
                  <div><p className="text-lg font-bold leading-none">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── TABS ── */}
        <div className="flex items-center gap-1 border-b overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn("px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap", activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ════════ OVERVIEW TAB ════════ */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Course Information</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                    {[
                      { l: "Category", v: course.category },
                      { l: "Series", v: course.series || "-" },
                      { l: "Catalog Suite", v: course.catalog_suite || "-" },
                      { l: "Difficulty", v: <Badge className={cn("text-[10px]", difficultyConfig[course.difficulty_level || "intermediate"])}>{(course.difficulty_level || "intermediate")}</Badge> },
                      { l: "Duration", v: `${course.duration_hours} hours` },
                      { l: "Instructor", v: course.instructor_name || "-" },
                      { l: "Version", v: course.version || "1.0" },
                      { l: "Created", v: new Date(course.created_at).toLocaleDateString() },
                      { l: "Updated", v: new Date(course.updated_at).toLocaleDateString() },
                    ].map((item, i) => (
                      <div key={i}><span className="text-[10px] text-muted-foreground block">{item.l}</span><span className="text-xs font-medium mt-0.5 block">{item.v}</span></div>
                    ))}
                  </div>
                  {(course.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t">
                      {course.tags.map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Access & Pricing */}
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Access & Pricing</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5 space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg border">
                    <AccessIcon className="w-5 h-5" />
                    <div><p className="text-sm font-medium">{ac.label}</p><p className="text-[10px] text-muted-foreground">{ac.desc}</p></div>
                  </div>
                  {course.access_model === "purchasable" && (
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                      <p className="text-2xl font-bold text-green-700">${(course.price || 0).toFixed(2)} <span className="text-xs font-normal">USD per learner</span></p>
                      {stats?.revenue > 0 && <p className="text-xs text-green-600 mt-1">Total Revenue: ${stats.revenue.toFixed(2)}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Hero Image */}
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><Image className="w-4 h-4" /> Hero Image</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5">
                  {course.hero_image_url ? (
                    <div className="relative rounded-lg overflow-hidden border">
                      <img src={course.hero_image_url} alt="Hero" className="w-full h-40 object-cover" />
                      {isAdmin && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button size="sm" variant="secondary" className="text-xs" onClick={() => heroInputRef.current?.click()} disabled={heroUploading} data-testid="hero-replace-btn">
                            {heroUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />} Replace
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : isAdmin ? (
                    <button onClick={() => heroInputRef.current?.click()} disabled={heroUploading}
                      className="w-full h-32 rounded-lg border-2 border-dashed border-muted-foreground/20 flex flex-col items-center justify-center gap-2 hover:border-primary/40 transition-colors" data-testid="hero-upload-btn">
                      {heroUploading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : <Image className="w-5 h-5 text-muted-foreground/40" />}
                      <span className="text-xs text-muted-foreground">{heroUploading ? "Uploading..." : "Upload hero image (up to 10MB)"}</span>
                    </button>
                  ) : (
                    <p className="text-xs text-muted-foreground">No hero image</p>
                  )}
                  <input ref={heroInputRef} type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" data-testid="hero-file-input" />
                </CardContent>
              </Card>

              {/* Supporting Documents */}
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Supporting Documents</CardTitle>
                    {isAdmin && (
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => docInputRef.current?.click()} disabled={docUploading} data-testid="doc-upload-btn">
                        {docUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add File
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  {(course.supporting_documents || []).length > 0 ? (
                    <div className="space-y-2">
                      {course.supporting_documents.map(doc => (
                        <div key={doc.id} className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-muted/30 transition-colors" data-testid={`doc-${doc.id}`}>
                          <div className="w-8 h-8 rounded bg-blue-50 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{doc.filename}</p>
                            <p className="text-[10px] text-muted-foreground">{(doc.size / 1024).toFixed(0)} KB</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <a href={doc.url} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`doc-view-${doc.id}`}><ExternalLink className="w-3.5 h-3.5" /></Button>
                            </a>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleDeleteDoc(doc.id)} data-testid={`doc-delete-${doc.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-4">No supporting documents uploaded</p>
                  )}
                  <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" onChange={handleDocUpload} className="hidden" data-testid="doc-file-input" />
                  <p className="text-[10px] text-muted-foreground mt-3">Accepted: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV (max 25MB)</p>
                </CardContent>
              </Card>
            </div>

            {/* Right column: Quick Actions */}
            <div className="space-y-5">
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Quick Actions</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5 space-y-2">
                  <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={() => { setEditing(true); setActiveTab("settings"); }} data-testid="quick-edit-btn">
                    <Edit className="w-4 h-4" /> Edit Settings
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={() => setShowAssign(true)} data-testid="quick-assign-btn">
                    <UserPlus className="w-4 h-4" /> Assign Users
                  </Button>
                  {isScorm && (
                    <>
                      <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={() => scormInputRef.current?.click()} disabled={scormUploading} data-testid="quick-upload-btn">
                        {scormUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload SCORM Package
                      </Button>
                      {course.scorm_entry_point && (
                        <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={handlePreviewScorm} data-testid="quick-preview-btn">
                          <Play className="w-4 h-4" /> Preview Course
                        </Button>
                      )}
                    </>
                  )}
                  <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={() => setActiveTab("analytics")} data-testid="quick-analytics-btn">
                    <BarChart3 className="w-4 h-4" /> View Analytics
                  </Button>
                </CardContent>
              </Card>

              {/* Completion Rules Summary */}
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Completion Rules</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5 space-y-2">
                  {[
                    { l: "Criteria", v: course.completion_criteria || "progress" },
                    { l: "Passing Score", v: `${course.passing_score || 0}%` },
                    { l: "Max Attempts", v: (course.max_attempts || 0) === 0 ? "Unlimited" : course.max_attempts },
                    { l: "Certificate", v: course.certificate_enabled ? "Yes" : "No" },
                    { l: "Expiry", v: (course.expiry_months || 0) === 0 ? "Never" : `${course.expiry_months} months` },
                    { l: "Renewal", v: course.renewal_required ? "Required" : "Not required" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0">
                      <span className="text-muted-foreground">{r.l}</span><span className="font-medium">{r.v}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ════════ COURSE BUILDER TAB ════════ */}
        {activeTab === "builder" && (
          <div className="space-y-5" data-testid="builder-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold">Course Builder</h2>
                <p className="text-xs text-muted-foreground">Add and reorder modules. Drag to rearrange or use arrows.</p>
              </div>
              <Button size="sm" className="text-xs h-8 gap-1.5" onClick={openAddModule} data-testid="add-module-btn">
                <Plus className="w-3.5 h-3.5" /> Add Module
              </Button>
            </div>

            {modulesLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : modules.length === 0 ? (
              <Card className="border shadow-none">
                <CardContent className="py-16 text-center">
                  <Layers className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                  <p className="text-sm font-medium">No modules yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Build your course by adding modules like SCORM, video, documents, assessments, and text sections</p>
                  <Button size="sm" className="mt-4 gap-1.5 text-xs" onClick={openAddModule} data-testid="empty-add-module-btn">
                    <Plus className="w-3.5 h-3.5" /> Add First Module
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="modules-list">
                {modules.map((mod, idx) => {
                  const moduleTypes = {
                    scorm: { icon: Zap, color: "bg-blue-50 text-blue-700 border-blue-200", label: "SCORM" },
                    video: { icon: Video, color: "bg-red-50 text-red-700 border-red-200", label: "Video" },
                    document: { icon: FileText, color: "bg-green-50 text-green-700 border-green-200", label: "Document" },
                    assessment: { icon: Shield, color: "bg-amber-50 text-amber-700 border-amber-200", label: "Assessment" },
                    ilt: { icon: Calendar, color: "bg-purple-50 text-purple-700 border-purple-200", label: "ILT Session" },
                    text: { icon: Type, color: "bg-gray-50 text-gray-700 border-gray-200", label: "Text/Info" },
                    quiz: { icon: Shield, color: "bg-orange-50 text-orange-700 border-orange-200", label: "Quiz" },
                  };
                  const mt = moduleTypes[mod.type] || moduleTypes.text;
                  const ModIcon = mt.icon;
                  return (
                    <Card
                      key={mod.id}
                      className={cn("border shadow-none transition-all", dragIndex === idx ? "opacity-50 scale-[0.98]" : "hover:border-primary/30")}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(idx)}
                      data-testid={`module-card-${mod.id}`}
                    >
                      <CardContent className="p-0">
                        <div className="flex items-center gap-0">
                          {/* Drag handle */}
                          <div className="w-10 flex items-center justify-center cursor-grab active:cursor-grabbing shrink-0 self-stretch border-r bg-muted/30 hover:bg-muted/60 transition-colors rounded-l-lg" data-testid={`module-drag-${mod.id}`}>
                            <GripVertical className="w-4 h-4 text-muted-foreground" />
                          </div>

                          {/* Order number */}
                          <div className="w-8 text-center shrink-0">
                            <span className="text-xs font-bold text-muted-foreground">{idx + 1}</span>
                          </div>

                          {/* Module icon */}
                          <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mx-2", mt.color)}>
                            <ModIcon className="w-4 h-4" />
                          </div>

                          {/* Module info */}
                          <div className="flex-1 min-w-0 py-3 pr-2">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium truncate">{mod.title}</p>
                              <Badge className={cn("text-[8px] px-1 py-0 border shrink-0", mt.color)}>{mt.label}</Badge>
                              {mod.is_required && <Badge variant="outline" className="text-[8px] px-1 py-0 shrink-0">Required</Badge>}
                              {mod.is_locked && <Badge className="text-[8px] px-1 py-0 bg-red-50 text-red-600 border-red-200 shrink-0">Locked</Badge>}
                            </div>
                            {mod.description && <p className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">{mod.description}</p>}
                            {mod.duration_minutes > 0 && <span className="text-[10px] text-muted-foreground"><Clock className="w-2.5 h-2.5 inline mr-0.5" />{mod.duration_minutes} min</span>}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5 pr-2 shrink-0">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleMoveModule(idx, "up")} disabled={idx === 0} data-testid={`module-up-${mod.id}`}>
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleMoveModule(idx, "down")} disabled={idx === modules.length - 1} data-testid={`module-down-${mod.id}`}>
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditModule(mod)} data-testid={`module-edit-${mod.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleDeleteModule(mod.id)} data-testid={`module-delete-${mod.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Summary */}
            {modules.length > 0 && (
              <Card className="border shadow-none">
                <CardContent className="p-4 flex items-center gap-6">
                  <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-muted-foreground" /><span className="text-sm font-medium">{modules.length} modules</span></div>
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /><span className="text-sm font-medium">{modules.reduce((a, m) => a + (m.duration_minutes || 0), 0)} min total</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-muted-foreground" /><span className="text-sm font-medium">{modules.filter(m => m.is_required).length} required</span></div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ════════ CONTENT & SCORM TAB ════════ */}
        {activeTab === "content" && isScorm && (
          <div className="space-y-5">
            {/* SCORM Player */}
            {showPlayer && scormPreviewUrl ? (
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Play className="w-4 h-4 text-primary" /> SCORM Course Player</CardTitle>
                  <Button variant="ghost" size="sm" className="h-7" onClick={() => setShowPlayer(false)}><X className="w-4 h-4" /></Button>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <ScormPreviewIframe courseId={courseId} entryPoint={course?.scorm_entry_point || "index_lms.html"} />
                </CardContent>
              </Card>
            ) : (
              <Card className="border shadow-none">
                <CardContent className="p-5">
                  {course.scorm_entry_point ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center"><Package className="w-7 h-7 text-blue-600" /></div>
                        <div>
                          <p className="text-sm font-semibold">SCORM Package Loaded</p>
                          <p className="text-xs text-muted-foreground">Version: {course.scorm_version || "1.2"} &middot; Entry: {course.scorm_entry_point}</p>
                          <p className="text-xs text-muted-foreground">Course Version: {course.version || "1.0"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button className="gap-1.5 text-xs" onClick={handlePreviewScorm} data-testid="launch-scorm-btn">
                          <Play className="w-4 h-4" /> Launch Preview
                        </Button>
                        <Button variant="outline" className="gap-1.5 text-xs" onClick={() => scormInputRef.current?.click()} disabled={scormUploading}>
                          {scormUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Update Package
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 border-2 border-dashed rounded-xl">
                      <FileUp className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" />
                      <h3 className="text-sm font-semibold mb-1">No SCORM Package Uploaded</h3>
                      <p className="text-xs text-muted-foreground mb-4">Upload a SCORM 1.2 or 2004 package (.zip) to enable course content</p>
                      <Button className="gap-1.5" onClick={() => scormInputRef.current?.click()} disabled={scormUploading} data-testid="upload-scorm-main-btn">
                        {scormUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload SCORM Package
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Version History */}
            {(course.scorm_versions || []).length > 0 && (
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> SCORM Version History</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="relative">
                    <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
                    <div className="space-y-3">
                      {(course.scorm_versions || []).slice().reverse().map((v, i) => (
                        <div key={i} className="relative pl-8">
                          <div className={cn("absolute left-1.5 top-2 w-3 h-3 rounded-full border-2 border-background", i === 0 ? "bg-primary" : "bg-muted-foreground/30")} />
                          <div className="p-3 rounded-lg border">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px] font-mono">v{v.version}</Badge>
                                <span className="text-sm font-medium">{v.filename}</span>
                                {i === 0 && <Badge className="bg-green-50 text-green-700 border-green-200 text-[9px]">Current</Badge>}
                              </div>
                              <span className="text-[10px] text-muted-foreground">{new Date(v.uploaded_at).toLocaleString()}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Uploaded by {v.uploaded_by_name || "Admin"} &middot; SCORM {v.scorm_version || "1.2"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ════════ ENROLLMENTS TAB ════════ */}
        {activeTab === "enrollments" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="relative max-w-xs flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search learners..." value={enrollSearch} onChange={e => setEnrollSearch(e.target.value)} className="pl-10 h-9 text-sm" />
              </div>
              <Button size="sm" className="gap-1.5 text-xs h-8" onClick={() => setShowAssign(true)} data-testid="enroll-users-btn">
                <UserPlus className="w-3.5 h-3.5" /> Enroll Users
              </Button>
            </div>

            <Card className="border shadow-none overflow-hidden" data-testid="enrollments-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="p-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Learner</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Progress</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Score</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Time</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Enrolled</th>
                      <th className="p-3 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEnroll.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No enrollments found</td></tr>
                    ) : filteredEnroll.map(e => (
                      <tr key={e.id} className="border-b hover:bg-muted/20">
                        <td className="p-3">
                          <div><p className="text-sm font-medium">{e.user_name}</p><p className="text-[10px] text-muted-foreground">{e.user_email}</p></div>
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={cn("text-[9px] px-1.5 py-0 border", {
                            "bg-green-50 text-green-700 border-green-200": e.status === "completed",
                            "bg-blue-50 text-blue-700 border-blue-200": e.status === "in_progress",
                            "bg-muted text-muted-foreground": e.status === "not_started",
                          })}>{e.status?.replace("_", " ")}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2 justify-center">
                            <Progress value={e.progress || 0} className="h-1.5 w-16" />
                            <span className="text-xs font-medium">{e.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="p-3 text-center"><span className="text-sm font-medium">{e.score ?? "-"}</span></td>
                        <td className="p-3 text-center"><span className="text-xs text-muted-foreground">{fmtTime(e.time_spent)}</span></td>
                        <td className="p-3 text-center"><span className="text-xs text-muted-foreground">{e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString() : "-"}</span></td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={() => handleRemoveEnrollment(e.id)} title="Remove">
                            <UserMinus className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t text-xs text-muted-foreground flex items-center justify-between">
                <span>Showing {filteredEnroll.length} of {enrollments.length} enrollments</span>
                <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={fetchEnrollments}><RefreshCw className="w-3 h-3" /> Refresh</Button>
              </div>
            </Card>
          </div>
        )}

        {/* ════════ ANALYTICS TAB ════════ */}
        {activeTab === "analytics" && (
          <div className="space-y-5">
            {stats ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { l: "Total Enrolled", v: stats.total_enrollments, icon: Users, color: "text-blue-600" },
                    { l: "Completed", v: stats.completed, icon: CheckCircle2, color: "text-green-600" },
                    { l: "In Progress", v: stats.in_progress, icon: Clock, color: "text-amber-600" },
                    { l: "Not Started", v: stats.not_started, icon: AlertTriangle, color: "text-muted-foreground" },
                  ].map((s, i) => (
                    <Card key={i} className="border shadow-none"><CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2"><s.icon className={cn("w-4 h-4", s.color)} /><span className="text-[10px] text-muted-foreground">{s.l}</span></div>
                      <p className="text-2xl font-bold">{s.v}</p>
                    </CardContent></Card>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Enrollment Funnel */}
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Enrollment Funnel</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5 space-y-3">
                      {[
                        { l: "Enrolled", v: stats.total_enrollments, pct: 100, c: "bg-blue-500" },
                        { l: "In Progress", v: stats.in_progress, pct: stats.total_enrollments > 0 ? (stats.in_progress / stats.total_enrollments) * 100 : 0, c: "bg-amber-500" },
                        { l: "Completed", v: stats.completed, pct: stats.total_enrollments > 0 ? (stats.completed / stats.total_enrollments) * 100 : 0, c: "bg-green-500" },
                      ].map((item, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium">{item.l}</span>
                            <span className="text-xs text-muted-foreground">{item.v} ({Math.round(item.pct)}%)</span>
                          </div>
                          <div className="h-6 rounded-lg bg-muted/50 overflow-hidden">
                            <div className={cn("h-full rounded-lg transition-all", item.c)} style={{ width: `${Math.max(item.pct, 2)}%` }} />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Performance */}
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Performance Metrics</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5 space-y-4">
                      {[
                        { l: "Completion Rate", v: `${stats.completion_rate}%`, bar: stats.completion_rate },
                        { l: "Average Progress", v: `${stats.avg_progress}%`, bar: stats.avg_progress },
                        { l: "Average Score", v: stats.avg_score, bar: stats.avg_score },
                      ].map((m, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">{m.l}</span>
                            <span className="text-sm font-bold">{m.v}</span>
                          </div>
                          <Progress value={m.bar} className="h-2" />
                        </div>
                      ))}
                      <div className="p-3 rounded-lg bg-muted/30 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Total Learning Time</span>
                        <span className="text-sm font-bold">{stats.total_time_hours}h</span>
                      </div>
                      {stats.revenue > 0 && (
                        <div className="p-3 rounded-lg bg-green-50 border border-green-200 flex items-center justify-between">
                          <span className="text-xs text-green-600 font-medium">Revenue Generated</span>
                          <span className="text-lg font-bold text-green-700">${stats.revenue.toFixed(2)}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Tenant Breakdown */}
                {stats.tenant_breakdown && Object.keys(stats.tenant_breakdown).length > 0 && (
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Tenant Breakdown</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <table className="w-full">
                        <thead><tr className="border-b bg-muted/30">
                          <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">Tenant</th>
                          <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Enrolled</th>
                          <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Completed</th>
                          <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Rate</th>
                        </tr></thead>
                        <tbody>
                          {Object.entries(stats.tenant_breakdown).map(([tid, data]) => (
                            <tr key={tid} className="border-b hover:bg-muted/20">
                              <td className="p-3 text-xs font-medium">{tid}</td>
                              <td className="p-3 text-center text-sm font-medium">{data.enrolled}</td>
                              <td className="p-3 text-center text-sm font-medium text-green-600">{data.completed}</td>
                              <td className="p-3 text-center text-sm font-medium">{data.enrolled > 0 ? Math.round((data.completed / data.enrolled) * 100) : 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
          </div>
        )}

        {/* ════════ SETTINGS TAB ════════ */}
        {activeTab === "settings" && (
          <Card className="border shadow-none">
            <CardHeader className="pt-4 pb-2 px-5 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Course Settings</CardTitle>
              {!editing ? (
                <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => setEditing(true)} data-testid="edit-settings-btn"><Edit className="w-3 h-3" /> Edit</Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => { setEditing(false); initEditForm(course); }}>Cancel</Button>
                  <Button size="sm" className="text-xs h-7 gap-1" onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="px-5 pb-5">
              {editing ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs">Title</Label><Input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} className="text-sm h-9" /></div>
                    <div><Label className="text-xs">Code</Label><Input value={editForm.code} onChange={e => setEditForm({...editForm, code: e.target.value})} className="text-sm h-9" /></div>
                  </div>
                  <div><Label className="text-xs">Description</Label><Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={3} className="text-sm" /></div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div><Label className="text-xs">Category</Label>
                      <Select value={editForm.category} onValueChange={v => setEditForm({...editForm, category: v})}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Course Type</Label>
                      <Select value={editForm.course_type} onValueChange={v => setEditForm({...editForm, course_type: v})}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scorm_1.2">eLearning (SCORM 1.2)</SelectItem>
                          <SelectItem value="scorm_2004">eLearning (SCORM 2004)</SelectItem>
                          <SelectItem value="ilt">Instructor-Led (ILT)</SelectItem>
                          <SelectItem value="video">Video</SelectItem>
                          <SelectItem value="document">Document</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Difficulty</Label>
                      <Select value={editForm.difficulty_level} onValueChange={v => setEditForm({...editForm, difficulty_level: v})}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem><SelectItem value="expert">Expert</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div><Label className="text-xs">Duration (hours)</Label><Input type="number" min="0.5" step="0.5" value={editForm.duration_hours} onChange={e => setEditForm({...editForm, duration_hours: e.target.value})} className="text-sm h-9" /></div>
                    <div><Label className="text-xs">Instructor</Label><Input value={editForm.instructor_name} onChange={e => setEditForm({...editForm, instructor_name: e.target.value})} className="text-sm h-9" /></div>
                    <div><Label className="text-xs">Tags (comma separated)</Label><Input value={editForm.tags} onChange={e => setEditForm({...editForm, tags: e.target.value})} className="text-sm h-9" /></div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="text-xs font-semibold mb-3">Access Model</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {Object.entries(accessModelConfig).map(([key, cfg]) => (
                        <button key={key} onClick={() => setEditForm({...editForm, access_model: key})}
                          className={cn("p-3 rounded-lg border-2 text-left transition-all", editForm.access_model === key ? "border-primary bg-primary/5" : "border-border hover:border-primary/30")}>
                          <cfg.icon className="w-4 h-4 mb-1" /><span className="text-xs font-medium block">{cfg.label}</span>
                          <span className="text-[10px] text-muted-foreground">{cfg.desc}</span>
                        </button>
                      ))}
                    </div>
                    {editForm.access_model === "purchasable" && (
                      <div className="mt-3 flex items-center gap-2 p-3 rounded-lg border-2 border-amber-200 bg-amber-50/30">
                        <span className="text-lg font-bold">$</span>
                        <Input type="number" min="0" step="0.01" value={editForm.price} onChange={e => setEditForm({...editForm, price: e.target.value})} className="text-sm h-9 max-w-[150px]" />
                        <span className="text-xs text-muted-foreground">USD per learner</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="text-xs font-semibold mb-3">Completion & Certification</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div><Label className="text-xs">Completion Criteria</Label>
                        <Select value={editForm.completion_criteria} onValueChange={v => setEditForm({...editForm, completion_criteria: v})}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="progress">Progress</SelectItem><SelectItem value="score">Min Score</SelectItem><SelectItem value="manual">Manual</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Passing Score (%)</Label><Input type="number" min="0" max="100" value={editForm.passing_score} onChange={e => setEditForm({...editForm, passing_score: e.target.value})} className="text-sm h-9" /></div>
                      <div><Label className="text-xs">Max Attempts (0=unlimited)</Label><Input type="number" min="0" value={editForm.max_attempts} onChange={e => setEditForm({...editForm, max_attempts: e.target.value})} className="text-sm h-9" /></div>
                      <div><Label className="text-xs">Expiry (months, 0=never)</Label><Input type="number" min="0" value={editForm.expiry_months} onChange={e => setEditForm({...editForm, expiry_months: e.target.value})} className="text-sm h-9" /></div>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-4">
                      {[
                        { id: "self_enroll", label: "Self-enrollment", key: "self_enrollment_enabled" },
                        { id: "cert", label: "Certificate on completion", key: "certificate_enabled" },
                        { id: "renewal", label: "Require renewal", key: "renewal_required" },
                      ].map(opt => (
                        <label key={opt.id} className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer">
                          <input type="checkbox" checked={editForm[opt.key]} onChange={e => setEditForm({...editForm, [opt.key]: e.target.checked})} className="rounded" />
                          <span className="text-xs font-medium">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {[
                    ["General", [
                      { l: "Category", v: course.category }, { l: "Series", v: course.series || "-" },
                      { l: "Catalog Suite", v: course.catalog_suite || "-" }, { l: "Difficulty", v: course.difficulty_level || "intermediate" },
                      { l: "Duration", v: `${course.duration_hours}h` }, { l: "Instructor", v: course.instructor_name || "-" },
                    ]],
                    ["Access", [
                      { l: "Model", v: ac.label }, { l: "Self-enrollment", v: course.self_enrollment_enabled !== false ? "Enabled" : "Disabled" },
                      ...(course.access_model === "purchasable" ? [{ l: "Price", v: `$${(course.price || 0).toFixed(2)}` }] : []),
                    ]],
                    ["Completion", [
                      { l: "Criteria", v: course.completion_criteria || "progress" }, { l: "Passing Score", v: `${course.passing_score || 0}%` },
                      { l: "Max Attempts", v: (course.max_attempts || 0) === 0 ? "Unlimited" : course.max_attempts },
                      { l: "Certificate", v: course.certificate_enabled ? "Enabled" : "Disabled" },
                      { l: "Expiry", v: (course.expiry_months || 0) === 0 ? "Never" : `${course.expiry_months} months` },
                      { l: "Renewal", v: course.renewal_required ? "Required" : "Not required" },
                    ]],
                  ].map(([section, items]) => (
                    <div key={section}>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{section}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {items.map((item, i) => (
                          <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border">
                            <span className="text-xs text-muted-foreground">{item.l}</span>
                            <span className="text-xs font-medium capitalize">{item.v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ════════ DISTRIBUTION TAB ════════ */}
        {activeTab === "distribution" && (
          <div className="space-y-5">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Tenant Distribution</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5">
                {course.access_model === "restricted" ? (
                  <div>
                    <p className="text-xs text-muted-foreground mb-3">This course is restricted. Only assigned tenants can access it.</p>
                    <div className="p-4 rounded-lg border-2 border-dashed">
                      {(course.tenant_assignments || []).length > 0 ? (
                        <div className="space-y-2">
                          {course.tenant_assignments.map(tid => (
                            <div key={tid} className="flex items-center justify-between p-2.5 rounded-lg border">
                              <span className="text-sm font-medium">{tid}</span>
                              <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]">Assigned</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <Shield className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
                          <p className="text-sm text-muted-foreground">No tenants assigned yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Globe className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
                    <p className="text-sm font-medium">Course is available to all tenants</p>
                    <p className="text-xs text-muted-foreground mt-1">Access model: {ac.label} — {ac.desc}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Enrollment by tenant */}
            {stats?.tenant_breakdown && Object.keys(stats.tenant_breakdown).length > 0 && (
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Enrollment by Tenant</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5 space-y-2">
                  {Object.entries(stats.tenant_breakdown).map(([tid, data]) => (
                    <div key={tid} className="flex items-center justify-between p-3 rounded-lg border">
                      <span className="text-sm font-medium">{tid}</span>
                      <div className="flex items-center gap-4 text-xs">
                        <span><Users className="w-3 h-3 inline mr-1" />{data.enrolled} enrolled</span>
                        <span className="text-green-600"><CheckCircle2 className="w-3 h-3 inline mr-1" />{data.completed} completed</span>
                        <span>{data.enrolled > 0 ? Math.round((data.completed / data.enrolled) * 100) : 0}%</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ════════ PREREQUISITES TAB ════════ */}
        {activeTab === "prerequisites" && (
          <div className="space-y-5" data-testid="prerequisites-tab">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Course Prerequisites</CardTitle>
                  <select className="h-8 rounded-md border px-2 text-xs bg-background" data-testid="add-prerequisite-select"
                    onChange={async (e) => {
                      const prereqCourseId = e.target.value;
                      if (!prereqCourseId) return;
                      const existing = prerequisites.map(p => p.course_id);
                      if (existing.includes(prereqCourseId)) { toast.error("Already added"); return; }
                      try {
                        await api.put(`/courses/${courseId}/prerequisites`, { prerequisites: [...existing.map(id => ({ course_id: id, type: "completion" })), { course_id: prereqCourseId, type: "completion" }] });
                        toast.success("Prerequisite added");
                        api.get(`/courses/${courseId}/prerequisites`).then(r => setPrerequisites(r.data.prerequisites || []));
                      } catch { toast.error("Failed"); }
                      e.target.value = "";
                    }}>
                    <option value="">+ Add Prerequisite...</option>
                    {availableCourses.filter(c => !prerequisites.find(p => p.course_id === c.id)).map(c => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                {prerequisites.length === 0 ? (
                  <div className="text-center py-8">
                    <Lock className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
                    <p className="text-sm text-muted-foreground">No prerequisites set</p>
                    <p className="text-xs text-muted-foreground mt-1">Learners can enroll directly in this course</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {prerequisites.map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`prereq-${i}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${p.is_met ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                            {p.is_met ? <CheckCircle2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{p.course_title}</p>
                            <p className="text-xs text-muted-foreground capitalize">Requires: {p.type}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={async () => {
                          const updated = prerequisites.filter((_, idx) => idx !== i).map(pr => ({ course_id: pr.course_id, type: pr.type }));
                          await api.put(`/courses/${courseId}/prerequisites`, { prerequisites: updated });
                          toast.success("Prerequisite removed");
                          api.get(`/courses/${courseId}/prerequisites`).then(r => setPrerequisites(r.data.prerequisites || []));
                        }}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ════════ DISCUSSIONS TAB ════════ */}
        {activeTab === "discussions" && (
          <div className="space-y-3" data-testid="discussions-tab">
            <Card className="border shadow-none">
              <CardContent className="p-5 text-center">
                <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium">Course Discussion Forum</p>
                <p className="text-xs text-muted-foreground mt-1">Open the full forum view to participate in discussions</p>
                <Button className="btn-primary mt-4" onClick={() => navigate(`/discussions/${courseId}`)} data-testid="open-discussions-btn">
                  Open Discussion Forum
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <input ref={scormInputRef} type="file" accept=".zip" className="hidden" onChange={handleScormUpload} />

      {/* Assignment Modal */}
      <AssignmentModal
        open={showAssign}
        onOpenChange={setShowAssign}
        title={course?.title || ""}
        onAssign={async (userIds) => {
          let s = 0, f = 0;
          for (const uid of userIds) { try { await api.post("/enrollments", { user_id: uid, course_id: courseId }); s++; } catch { f++; } }
          if (s > 0) toast.success(`Enrolled ${s} users`);
          if (f > 0) toast.error(`${f} failed`);
          fetchEnrollments(); fetchStats();
        }}
      />

      {/* Module Create/Edit Dialog */}
      {showModuleDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModuleDialog(false)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="module-dialog">
            <div className="p-5 border-b">
              <h3 className="text-base font-semibold">{editModule ? "Edit Module" : "Add Module"}</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="text-xs">Module Title</Label>
                <Input value={moduleForm.title} onChange={e => setModuleForm({ ...moduleForm, title: e.target.value })} className="h-8 text-sm" placeholder="e.g., Introduction to Safety" data-testid="module-title-input" />
              </div>
              <div>
                <Label className="text-xs mb-2 block">Module Type</Label>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { key: "text", label: "Text", icon: Type },
                    { key: "scorm", label: "SCORM", icon: Zap },
                    { key: "video", label: "Video", icon: Video },
                    { key: "document", label: "Document", icon: FileText },
                    { key: "assessment", label: "Assessment", icon: Shield },
                    { key: "ilt", label: "ILT", icon: Calendar },
                    { key: "quiz", label: "Quiz", icon: Shield },
                  ].map(t => (
                    <button key={t.key} onClick={() => setModuleForm({ ...moduleForm, type: t.key })}
                      className={cn("p-2 rounded-lg border text-center text-[10px] font-medium transition-all", moduleForm.type === t.key ? "border-primary bg-primary/5 text-primary" : "hover:border-primary/30")}
                      data-testid={`module-type-${t.key}`}>
                      <t.icon className="w-4 h-4 mx-auto mb-0.5" />{t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={moduleForm.description} onChange={e => setModuleForm({ ...moduleForm, description: e.target.value })} className="h-8 text-sm" placeholder="Brief description..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Duration (min)</Label>
                  <Input type="number" value={moduleForm.duration_minutes} onChange={e => setModuleForm({ ...moduleForm, duration_minutes: parseInt(e.target.value) || 0 })} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Content ID/URL</Label>
                  <Input value={moduleForm.content_url || moduleForm.content_id} onChange={e => setModuleForm({ ...moduleForm, content_url: e.target.value })} className="h-8 text-sm" placeholder="Optional reference" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs p-2 rounded-lg border">
                <input type="checkbox" checked={moduleForm.is_required} onChange={e => setModuleForm({ ...moduleForm, is_required: e.target.checked })} className="rounded" />
                <span className="font-medium">Required module — must be completed for course completion</span>
              </label>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowModuleDialog(false)}>Cancel</Button>
              <Button size="sm" className="text-xs gap-1" onClick={handleSaveModule} disabled={moduleSaving} data-testid="save-module-btn">
                {moduleSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} {editModule ? "Update" : "Add Module"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SCORM Upload Progress Dialog */}
      <Dialog open={showScormUploadDialog} onOpenChange={(v) => !scormUploading && setShowScormUploadDialog(v)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {scormUploadStage === "done" ? "Upload Complete" : scormUploadStage === "error" ? "Upload Failed" : "Processing SCORM Package"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-6" data-testid="scorm-upload-progress">
            {/* Circular progress */}
            <div className="flex justify-center mb-6">
              <div className="relative w-28 h-28">
                <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/20" />
                  <circle cx="50" cy="50" r="42" fill="none" strokeWidth="6"
                    className={scormUploadStage === "done" ? "text-emerald-500" : scormUploadStage === "error" ? "text-red-500" : "text-primary"}
                    strokeDasharray={264}
                    strokeDashoffset={264 - (264 * scormUploadProgress / 100)}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 0.4s ease" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  {scormUploadStage === "done" ? (
                    <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                  ) : scormUploadStage === "error" ? (
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  ) : (
                    <>
                      <span className="text-2xl font-bold tracking-tight">{scormUploadProgress}%</span>
                      <span className="text-[10px] text-muted-foreground">completed</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Stage steps */}
            <div className="space-y-3 max-w-xs mx-auto">
              {[
                { key: "prepare", label: "Preparing upload", desc: "Reading file and creating upload stream" },
                { key: "upload", label: "Uploading to server", desc: "Transferring SCORM zip to the server" },
                { key: "extract", label: "Extracting contents", desc: "Unzipping package and locating manifest" },
                { key: "parse", label: "Parsing manifest", desc: "Reading imsmanifest.xml for metadata" },
                { key: "validate", label: "Validating structure", desc: "Verifying SCORM compliance and entry point" },
                { key: "done", label: "Complete", desc: "SCORM package ready for learners" },
              ].map((step, i) => {
                const stageOrder = ["prepare", "upload", "extract", "parse", "validate", "done"];
                const currentIdx = stageOrder.indexOf(scormUploadStage === "error" ? "upload" : scormUploadStage);
                const stepIdx = stageOrder.indexOf(step.key);
                const isActive = stepIdx === currentIdx;
                const isDone = stepIdx < currentIdx || scormUploadStage === "done";

                return (
                  <div key={step.key} className="flex items-start gap-3" data-testid={`upload-stage-${step.key}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-all duration-300 ${
                      isDone ? "bg-emerald-500 text-white" :
                      isActive ? "bg-primary text-primary-foreground ring-4 ring-primary/20" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      {isDone ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : isActive ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <span className="text-[10px] font-bold">{i + 1}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium transition-colors ${isDone ? "text-emerald-600 dark:text-emerald-400" : isActive ? "text-foreground" : "text-muted-foreground"}`}>
                        {step.label}
                      </p>
                      {(isActive || isDone) && (
                        <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Module Request Dialog */}
      <Dialog open={showCustomModuleDialog} onOpenChange={setShowCustomModuleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" /> Request Custom Module
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Request a customized version of <strong>{course?.title}</strong> tailored to your organization. Our team will review your requirements and provide a proposal.
            </p>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">What customizations do you need?</label>
              <Textarea
                value={customModuleDesc}
                onChange={(e) => setCustomModuleDesc(e.target.value)}
                rows={4}
                placeholder="Describe your specific needs: custom branding, modified content, additional modules, localization, industry-specific scenarios..."
                data-testid="custom-module-desc"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Priority</label>
              <div className="flex gap-2">
                {["normal", "high", "urgent"].map((p) => (
                  <button key={p} onClick={() => setCustomModulePriority(p)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      customModulePriority === p
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-muted text-muted-foreground border-border hover:border-blue-300"
                    }`} data-testid={`priority-${p}`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30">
              <p className="text-xs text-blue-800 dark:text-blue-300">
                Custom modules may be billable depending on scope. Our team will provide pricing before any work begins.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomModuleDialog(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCustomModuleRequest} disabled={submittingRequest} data-testid="submit-custom-module">
              {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Course"
        description={`This will permanently delete "${course?.title}" and all associated enrollments, progress data, SCORM content, and modules.`}
        confirmWord="DELETE"
        onConfirm={handleDelete}
        confirmLabel="Delete Course"
      />
    </AdminLayout>
  );
};

export default CourseDetailPage;
